/** A page reads its kept view with useView and shows its age with ViewFreshness (rule U-36). */

import type { Auth } from "firebase/auth";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { ApiError } from "../api";
import { clockTime } from "../format";
import { RefreshFailed, refreshFailure } from "../RefreshFailed";
import { failedText, keptText } from "./accounts";
import { getVersion, subscribe } from "./store";
import { openState, type RunState, runView, type Runner } from "./viewRunner";
import { keepView, keptView } from "./views";

export interface ViewState<T> extends RunState {
  data: T | undefined;
  /** The client time of the request of the kept data, or null. */
  at: number | null;
  reload: () => void;
  /** Keep the answer of a write as the view. */
  keep: (next: T | ((before: T) => T)) => void;
}

const IDLE: RunState = { checking: false, checked: false, error: null };
const noRunner = { state: () => IDLE, subscribe: () => () => undefined };

/** The failure text key of an error, as the pages show it. */
export function loadFailure(e: unknown): "session" | "server" | "network" {
  const code = e instanceof ApiError ? e.status : 0;
  return code === 401 ? "session" : code >= 500 ? "server" : "network";
}

/** The kept view `key` (null: the page may not load it), fetched with `load` when it is stale, old or missing. */
export function useView<T>(key: string | null, load: (signal: AbortSignal) => Promise<T>): ViewState<T> {
  const loadRef = useRef(load);
  loadRef.current = load;
  const [runner, setRunner] = useState<Runner | null>(null);
  useEffect(() => {
    if (key === null) return;
    const started = runView<T>(key, (signal) => loadRef.current(signal));
    setRunner(started);
    return () => {
      started.stop();
      setRunner((now) => (now === started ? null : now));
    };
  }, [key]);
  const source = runner ?? noRunner;
  const running = useSyncExternalStore(source.subscribe, source.state, source.state);
  const version = useSyncExternalStore(subscribe, getVersion, getVersion);
  const kept = useMemo(() => (key === null ? null : keptView<T>(key)), [key, version]);
  const opening = useMemo(() => (key === null ? IDLE : openState(key)), [key, version]);
  const run = runner ? running : opening;
  return {
    ...run,
    data: kept?.data,
    at: kept?.at ?? null,
    reload: () => runner?.reload(),
    keep: (next) => {
      if (key === null) return;
      const before = keptView<T>(key)?.data;
      const value = typeof next === "function" ? (before === undefined ? undefined : (next as (old: T) => T)(before)) : next;
      if (value !== undefined) keepView(key, value, Date.now());
    },
  };
}

/** "Updated 10:42 AM", "Updated 3 min ago · checking…", or the age of a view that could not be checked, with the red alert. */
export function ViewFreshness({
  view,
  timeZone,
  auth,
}: {
  view: Pick<ViewState<unknown>, "at" | "checking" | "checked" | "error" | "reload">;
  timeZone?: string;
  auth: Auth;
}) {
  if (view.at === null) return null;
  const failed = view.error && !view.checking ? refreshFailure(view.error) : null;
  return (
    <>
      <p className="sub" data-testid="view-freshness">
        {failed ? (
          <span data-testid="view-stale">{failedText(view.at, timeZone)}</span>
        ) : view.checked && !view.checking ? (
          <span data-testid="view-updated">Updated {clockTime(new Date(view.at), timeZone)}</span>
        ) : (
          <span data-testid="view-kept">{keptText(view.at)}</span>
        )}
      </p>
      {failed && <RefreshFailed failed={failed} at={view.at} timeZone={timeZone} auth={auth} retry={view.reload} />}
    </>
  );
}
